import { FILE_ACCEPT_MAP } from "../constants";

export const getFileExtension = (fileName = "") => {
  const ext = fileName.split(".").pop();
  return (ext || "").trim().toLowerCase();
};

export const MIME_TO_EXT = {
  [FILE_ACCEPT_MAP.PDF]: "pdf",
  [FILE_ACCEPT_MAP.XLS]: "xls",
  [FILE_ACCEPT_MAP.XLSX]: "xlsx",
  [FILE_ACCEPT_MAP.DOC]: "doc",
  [FILE_ACCEPT_MAP.DOCX]: "docx",
  [FILE_ACCEPT_MAP.PNG]: "png",
  [FILE_ACCEPT_MAP.JPG]: "jpg",
  [FILE_ACCEPT_MAP.JPEG]: "jpeg",
  [FILE_ACCEPT_MAP.WEBP]: "webp",
  [FILE_ACCEPT_MAP.GIF]: "gif",
  [FILE_ACCEPT_MAP.SVG]: "svg",
};

export const getServerFormat = (file) => {
  if (!file) return "";

  const ext = getFileExtension(file.name);
  if (ext) return ext;

  const mime = (file.type || "").toLowerCase();
  return MIME_TO_EXT[mime] || "";
};

export const isAcceptedMime = (file, accept = "") => {
  const acceptedTypes = accept.split(",").map((t) => t.trim().toLowerCase());
  const mime = (file.type || "").toLowerCase();

  return acceptedTypes.some((a) => {
    if (a.endsWith("/*")) return mime.startsWith(a.replace("/*", "/"));
    return mime === a;
  });
};

export const getBaseName = (fileName = "") => {
  const parts = fileName.split(".");
  parts.pop(); // remove extension
  return parts.join(".") || fileName;
};

export const sanitizeName = (s = "") =>
  String(s)
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w\-]/g, "_"); // keep safe chars only

// We’ll append a "tag" at the end so server accepts it as normal name.
export const buildServerName = ({
  originalFileName,
  groupKey,
  slotIndex,
  providerId,
}) => {
  const base = sanitizeName(getBaseName(originalFileName));

  // tag format (simple + parseable)
  // __g_<group>__s_<slot>__p_<provider>  (provider optional)
  const g = sanitizeName(groupKey || "default");
  const s = Number.isFinite(slotIndex) ? slotIndex : 0;
  const p = providerId ? sanitizeName(providerId) : "";

  const tag = p ? `__g_${g}__s_${s}__p_${p}` : `__g_${g}__s_${s}`;

  // keep reasonable length (optional)
  const MAX = 120;
  const name = `${base}${tag}`;
  return name.length > MAX ? `${base.slice(0, MAX - tag.length)}${tag}` : name;
};

// Parse meta back from server doc.name
export const parseServerNameMeta = (name = "") => {
  const str = String(name);

  const g = str.match(/__g_([^_]+(?:_[^_]+)*)/); // greedy-ish
  const s = str.match(/__s_(\d+)/);
  const p = str.match(/__p_([^_]+(?:_[^_]+)*)/);

  return {
    groupKey: g ? g[1] : null,
    slotIndex: s ? Number(s[1]) : null,
    providerId: p ? p[1] : null,
  };
};

export const resolveFileFormat = (file) => {
  // Case 1: freshly selected file
  if (file?.type) return file.type;

  // Case 2: server uploaded document (format: "xlsx")
  if (file) {
    return FILE_ACCEPT_MAP[file.toUpperCase()] || file;
  }

  // Case 3: fallback from name/path
  const ext = file?.name?.split(".").pop()?.toUpperCase();
  if (ext && FILE_ACCEPT_MAP[ext]) {
    return FILE_ACCEPT_MAP[ext];
  }

  return FILE_ACCEPT_MAP.PDF;
};

// local UI key builder (consistent across app)
export const buildLocalFileKey = ({
  groupKey,
  documentType,
  slotIndex,
  providerId,
}) => {
  const g = String(groupKey || "default").trim();
  const d = String(documentType || "unknown").trim();
  const s = Number.isFinite(slotIndex) ? slotIndex : 0;
  const p = providerId ? String(providerId).trim() : null;

  return p ? `${g}-${d}-${s}-${p}` : `${g}-${d}-${s}`;
};
